import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControl,
  Backdrop,
  CircularProgress,
  Collapse,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { runSimulator } from "../../../controllers/PriceEvaluateController";
import { getAllLocations, getCatalogs, getCustomerGroups, getRequestSources, getSkusByCatalog } from "../../../controllers/CatalogController";
import { flattenLocationStores } from "../../../utils/locations";
import { getRules } from "../../../controllers/RuleController";
import Message from "../../../components/Message";

const ALL_SOURCE = "All";
const ALL_GROUP = { id: "__ALL__", name: "All" };
const ALL_STORE = { storeId: "__ALL__", storeName: "All" };
const ALL_GEO_TAG = { storeId: "__ALL__", storeName: "All" };

export default function PriceSimulator() {

  const [context, setContext] = useState({
    source: ALL_SOURCE,
    customerLat: "",
    customerLong: "",
    groups: [ALL_GROUP],
    stores: [ALL_STORE],
    geoTag: [ALL_GEO_TAG]
  });

  const [catalogList, setCatalogList] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleDetails, setRuleDetails] = useState({
    "items": [],
    "env": "",
    "catalogIds": [],
    "rules": [],
  })
  const [rules, setRules] = useState([]);
  const [ruleSearchText, setRuleSearchText] = useState("");
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState([]);

  const [apiMessage, setApiMessage] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [stores, setStores] = useState([]);
  const [geoTags, setGeotags] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    getCatalogList();
    fetchCustomerGroups();
    fetchRequestSources();
    fetchAllLocations()
  }, []);

  useEffect(() => {
    setPage(1);
  }, [results]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRules(ruleSearchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [ruleSearchText, ruleDetails.catalogIds]);

  useEffect(() => {
    if (!(ruleDetails?.catalogIds?.length || ruleDetails?.rules?.length)) return;
    setRuleDetails(prev => {
      if (prev.catalogIds?.length) return prev;

      return {
        ...prev,
        items: []
      };
    });

    fetchProducts();

  }, [ruleDetails.rules]);

  useEffect(() => {
    setRuleDetails(prev => ({
      ...prev,
      rules: [],
      items: []
    }));
    setProducts([]);
  }, [ruleDetails.catalogIds]);

  const fetchRules = async (searchText = "") => {
    setLoading(true);
    try {
      const res = await getRules({
        catalogId: ruleDetails?.catalogIds.map(cat => cat.catalogId),
        query: searchText,
      });
      setRules(res?.content || []);
    } finally {
      setLoading(false);
    }
  }

  const fetchAllLocations = async () => {
    const res = await getAllLocations();
    const storeList = flattenLocationStores(res);

    const geoTagList = Object.entries(res).flatMap(([catalogId, val]) =>
      val.geoTags.map((g) => ({ catalogId, ...g }))
    );
    setStores(storeList);
    setGeotags(geoTagList);
  }


  const fetchProducts = async () => {
    setLoading(true);
    const catalogIds =
      (ruleDetails?.catalogIds?.length
        ? ruleDetails.catalogIds.map(cat => cat.catalogId)
        : ruleDetails?.rules?.map(rule => rule.catalogId)) || [];

    const res = await getSkusByCatalog({ catalogId: catalogIds });
    setProducts(res);
    setLoading(false);
  }

  const getCatalogList = async () => {
    try {
      setLoading(true);
      const res = await getCatalogs();
      if (res && res.length > 0) {
        setCatalogList(res);
      }
      setLoading(false);
    } catch (err) {
      setCatalogList([]);
      setLoading(false);
      console.log(err);
    }
  };

  const fetchCustomerGroups = async () => {
    try {
      setLoading(true);
      const res = await getCustomerGroups();
      setLoading(false);
      setCustomerGroups(res);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  }

  const fetchRequestSources = async () => {
    try {
      setLoading(true);
      const res = await getRequestSources();
      setLoading(false);
      setSources(res);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  }

  const handleSourceChange = (e, v) => {
    setContext({ ...context, source: v || ALL_SOURCE });
  };

  const handleExclusiveAllChange = (field, allOption, idKey) => (e, v) => {
    const previousHadAll = context[field].some(item => item[idKey] === allOption[idKey]);
    const newHasAll = v.some(item => item[idKey] === allOption[idKey]);

    let newValue = v;
    if (newHasAll && !previousHadAll) {
      newValue = [allOption];
    } else if (newHasAll && previousHadAll && v.length > 1) {
      newValue = v.filter(item => item[idKey] !== allOption[idKey]);
    }

    if (newValue.length === 0) {
      newValue = [allOption];
    }

    setContext({ ...context, [field]: newValue });
  };

  const renderTagsWithoutAllDelete = (allOption, idKey, getLabel) => (value, getTagProps) =>
    value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      const isAll = option[idKey] === allOption[idKey];

      if (isAll) {
        return (
          <Typography key={key} sx={{ ml: 1, fontSize: 14 }}>
            {getLabel(option)}
          </Typography>
        );
      }

      return (
        <Chip
          {...tagProps}
          key={key}
          label={getLabel(option)}
          size="small"
        />
      );
    });

  const isAllSelected = (list, allOption, idKey) =>
    list?.some(item => item[idKey] === allOption[idKey]);

  const onClickRunSimulator = async () => {
    setLoading(true);
    try {
      const postData = {
        context: {
          ...context,
          source: context.source === ALL_SOURCE ? null : context.source,
          groups: isAllSelected(context.groups, ALL_GROUP, "id")
            ? []
            : context.groups?.map(g => g.id) || [],
          stores: isAllSelected(context.stores, ALL_STORE, "storeId")
            ? []
            : context.stores?.map(s => s.storeId) || [],
          geoTag: isAllSelected(context.geoTag, ALL_GEO_TAG, "storeId")
            ? []
            : context.geoTag
        },
        requestedProducts: ruleDetails?.items?.map(itm => ({
          productItemId: String(itm.productItemId)
        })) || [],
        catalogIds: ruleDetails?.catalogIds?.map(c => c.catalogId) || [],
        ruleIds: ruleDetails?.rules?.map(r => r.id) || []
      };

      const response = await runSimulator(postData);
      setResults(response);
    } catch (err) {
      setResults(null);
      setApiMessage({
        open: true,
        type: "error",
        message: err?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(() => {
    return Math.ceil((results?.length || 0) / pageSize);
  }, [results]);

  const paginatedResults = useMemo(() => {
    return results?.slice((page - 1) * pageSize, page * pageSize);
  }, [results, page]);

  const RuleEntryRow = ({ rule, idx }) => {
    const isWinner = idx === 0;
    const [open, setOpen] = useState(isWinner);

    return (
      <Box
        sx={{
          borderLeft: isWinner ? "4px solid #f5a623" : "4px solid transparent",
          backgroundColor: isWinner ? "#fff8e6" : "white"
        }}
      >
        <Box
          onClick={() => setOpen((o) => !o)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            cursor: "pointer"
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Chip
              size="small"
              label={isWinner ? "Winner" : `Other Rule ${idx}`}
              sx={{
                fontWeight: 600,
                backgroundColor: isWinner ? "#f5a623" : "#E5E7EB",
                color: isWinner ? "white" : "#64748B"
              }}
            />
            <Typography fontSize={15} color="#64748B">
              Rule ID: <strong>{rule.ruleId ?? "-"}</strong>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontSize={20} fontWeight={isWinner ? 700 : 400}>
              ₹{rule.ruleAppliedPrice ?? "-"}
            </Typography>
            <IconButton size="small" sx={{ p: 0 }}>
              <ExpandMoreIcon
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s"
                }}
              />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={open}>
          <Box display="flex" justifyContent="space-between" sx={{ px: 2.5, pb: 2.5 }}>
            <Box>
              <Typography fontSize={15} color="#64748B">
                Rule ID
              </Typography>
              <Typography>{rule.ruleId ?? "-"}</Typography>
            </Box>

            <Box>
              <Typography fontSize={15} color="#64748B">
                Rule Type
              </Typography>
              <Typography>{rule.ruleType ?? "-"}</Typography>
            </Box>

            <Box>
              <Typography fontSize={15} color="#64748B">
                Priority
              </Typography>
              <Typography>{rule.priority ?? "-"}</Typography>
            </Box>

            <Box textAlign="right">
              <Typography fontSize={15} color="#64748B">
                Rule Version
              </Typography>
              <Typography>{rule.ruleVersion ?? "-"}</Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const RuleEntryList = ({ entries }) => {
    if (!entries?.length) {
      return (
        <Box sx={{ backgroundColor: "white", p: 3 }}>
          <Typography color="#64748B">No rule applied</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ backgroundColor: "white" }}>
        {entries.map((rule, idx) => (
          <Box
            key={`${rule.ruleId}-${idx}`}
            sx={{
              borderBottom: idx < entries.length - 1 ? "1px solid #EEF2F7" : "none",
            }}
          >
            <RuleEntryRow rule={rule} idx={idx} />
          </Box>
        ))}
      </Box>
    );
  };

  const SectionHeader = ({ label, color, backgroundColor }) => (
    <Box
      sx={{
        p: 2,
        backgroundColor,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color }}>
        {label}
      </Typography>
    </Box>
  );

  const ProductResultCard = ({ item }) => {
    const [cardOpen, setCardOpen] = useState(false);
    const stagingWinnerPrice = item.staging?.[0]?.ruleAppliedPrice;
    const productionWinnerPrice = item.production?.[0]?.ruleAppliedPrice;

    return (
      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid #E5E7EB",
          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
          overflow: "hidden",
          height: "100%"
        }}
      >

        {/* PRODUCT HEADER */}
        <Box
          onClick={() => setCardOpen((o) => !o)}
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f1f4f9",
            borderBottom: "1px solid #EEF2F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer"
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" sx={{ flex: 1 }}>
            <Box sx={{ width: { xs: "50%", md: "10%" } }}>
              <Typography fontSize={15} color="#64748B">
                Product Item Id
              </Typography>

              <Typography fontWeight={600} color="#7e77ca">
                {item.productItemId}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "50%", md: "50%" } }}>
              <Typography fontSize={15} color="#64748B">
                Product Name
              </Typography>

              <Typography fontWeight={600} color="#7e77ca">
                {item.productItemName}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "50%", md: "10%" } }}>
              <Typography fontSize={15} color="#64748B">
                MRP
              </Typography>

              <Typography fontWeight={600}>
                ₹{item.mrp ?? "-"}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "50%", md: "10%" } }}>
              <Typography fontSize={15} color="#64748B">
                Draft Price
              </Typography>

              <Typography fontWeight={600} color="#7e77ca">
                {stagingWinnerPrice != null ? `₹${stagingWinnerPrice}` : "-"}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "50%", md: "10%" } }}>
              <Typography fontSize={15} color="#64748B">
                Published Price
              </Typography>

              <Typography fontWeight={600} color="#17a27b">
                {productionWinnerPrice != null ? `₹${productionWinnerPrice}` : "-"}
              </Typography>
            </Box>
          </Box>

          <IconButton size="small">
            <ExpandMoreIcon
              sx={{
                transform: cardOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s"
              }}
            />
          </IconButton>
        </Box>

        {/* RESULT SECTION */}
        <Collapse in={cardOpen}>
          <Grid container spacing={2} sx={{ p: 3 }}>

            {/* STAGING */}
            <Grid item size={{ xs: 6, md: 6 }}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#F9FAFB",
                  overflow: "hidden"
                }}
              >
                <SectionHeader label="STAGING" color="#7e77ca" backgroundColor="#eeeefa" />

                <RuleEntryList entries={item.staging} />
              </Box>
            </Grid>

            {/* PRODUCTION */}
            {item.production?.length > 0 && (
              <Grid item size={{ xs: 6, md: 6 }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#F9FAFB",
                    overflow: "hidden"
                  }}
                >
                  <SectionHeader label="PRODUCTION" color="#17a27b" backgroundColor="#f0f9f6" />

                  <RuleEntryList entries={item.production} />
                </Box>
              </Grid>
            )}

          </Grid>
        </Collapse>
      </Card>
    );
  };

  const ResultCards = React.memo(({ results }) => {
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        {results.map((item, index) => (
          <Grid item size={{ xs: 12, md: 12 }} key={index}>
            <ProductResultCard item={item} />
          </Grid>
        ))}
      </Grid>
    );
  });

  return (
    <Box>
      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 999,
          color: "#fff",
          backdropFilter: "blur(3px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Card
        sx={{
          margin: "auto",
          borderRadius: 3,
          boxShadow: 3
        }}
      >
        <CardContent>

          {/* Header */}

          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h5">Rule Simulator</Typography>

            <Button variant="contained" onClick={onClickRunSimulator}
              disabled={ruleDetails.catalogIds.length == 0 && ruleDetails.rules.length == 0}
            >
              Run Simulator
            </Button>
          </Box>



          <Divider sx={{ mb: 2 }} />

          <>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "#333"
              }}
            >
              Context Details
            </Typography>

            <Grid container spacing={2}>

              <Grid item size={{ xs: 6, md: 2 }}>
                <FormControl size="small" fullWidth>
                  <Autocomplete
                    options={[ALL_SOURCE, ...(sources || [])]}
                    value={context.source || ALL_SOURCE}
                    onChange={handleSourceChange}
                    disableClearable={context.source === ALL_SOURCE}
                    renderInput={(params) => (
                      <TextField {...params} label="Source" size="small" />
                    )}
                  />
                </FormControl>

              </Grid>
              <Grid item size={{ xs: 6, md: 2 }}>
                <TextField
                  label="Latitude"
                  size="small"
                  fullWidth
                  value={context.customerLat}
                  onChange={(e) =>
                    setContext({ ...context, customerLat: e.target.value })
                  }
                />
              </Grid>

              <Grid item size={{ xs: 6, md: 2 }}>
                <TextField
                  label="Longitude"
                  size="small"
                  fullWidth
                  value={context.customerLong}
                  onChange={(e) =>
                    setContext({ ...context, customerLong: e.target.value })
                  }
                />
              </Grid>

              <Grid item size={{ xs: 6, md: 2 }}>
                <FormControl size="small" fullWidth>
                  <Autocomplete
                    multiple
                    options={[ALL_GROUP, ...(customerGroups || [])]}
                    value={context.groups || []}
                    onChange={handleExclusiveAllChange("groups", ALL_GROUP, "id")}
                    disableClearable={isAllSelected(context.groups, ALL_GROUP, "id")}
                    getOptionLabel={(option) => option?.id === ALL_GROUP.id ? option.name : option?.id && option?.name ? `${option.id} - ${option.name}` : option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderTags={renderTagsWithoutAllDelete(ALL_GROUP, "id", (option) => option?.id === ALL_GROUP.id ? option.name : option?.id && option?.name ? `${option.id} - ${option.name}` : option?.name || "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Groups" size="small" />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 6, md: 2 }}>
                <FormControl size="small" fullWidth>
                  <Autocomplete
                    multiple
                    options={[ALL_STORE, ...stores]}
                    value={context.stores}
                    onChange={handleExclusiveAllChange("stores", ALL_STORE, "storeId")}
                    disableClearable={isAllSelected(context.stores, ALL_STORE, "storeId")}
                    getOptionLabel={(option) => option?.storeId === ALL_STORE.storeId ? option.storeName : option?.storeId && option?.storeName ? `${option.storeId} - ${option.storeName}` : option?.storeName || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.storeId === value.storeId
                    }
                    renderTags={renderTagsWithoutAllDelete(ALL_STORE, "storeId", (option) => option?.storeId === ALL_STORE.storeId ? option.storeName : option?.storeId && option?.storeName ? `${option.storeId} - ${option.storeName}` : option?.storeName || "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Store" size="small" />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    multiple
                    options={[ALL_GEO_TAG, ...geoTags]}
                    value={context.geoTag}
                    onChange={handleExclusiveAllChange("geoTag", ALL_GEO_TAG, "storeId")}
                    disableClearable={isAllSelected(context.geoTag, ALL_GEO_TAG, "storeId")}
                    getOptionLabel={(option) => option?.storeName || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.storeId === value.storeId
                    }
                    renderTags={renderTagsWithoutAllDelete(ALL_GEO_TAG, "storeId", (option) => option?.storeName || "")}
                    renderInput={(params) =>
                      <TextField {...params} label="Geo Tag" size="small" />
                    }
                  />

                </FormControl>
              </Grid>

            </Grid>
            <Divider sx={{ marginY: 2 }} />

            <Typography fontWeight={600} mb={2}>
              Catalog / Rules/ Product Selection
              <Typography variant="caption" style={{ marginleft: '2px' }} color="primary"> (Either Catalog or Rule should be selected)</Typography>
            </Typography>

            <Grid container spacing={2}>
              <Grid item size={{ xs: 3, md: 2 }}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    multiple
                    options={catalogList}
                    value={ruleDetails.catalogIds}
                    onChange={(e, v) => setRuleDetails({ ...ruleDetails, catalogIds: v })}
                    getOptionLabel={(option) => option?.catalogName || ""}
                    isOptionEqualToValue={(option, value) => option?.catalogId === value?.catalogId}
                    renderInput={(params) => (
                      <TextField {...params} label="Catalogs" size="small" />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 3, md: 2 }}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    multiple
                    options={rules}
                    value={ruleDetails.rules}
                    onChange={(e, v) => setRuleDetails({ ...ruleDetails, rules: v })}
                    getOptionLabel={(option) => `${option?.id}-${option?.description}` || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    inputValue={ruleSearchText}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setRuleSearchText(value);
                      }
                    }}
                    renderInput={(params) =>
                      <TextField {...params} label="Rules" size="small" />
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 3, md: 3 }}>
                <FormControl fullWidth size="small">
                  <Autocomplete
                    multiple
                    options={products}
                    value={ruleDetails.items}
                    onChange={(e, v) => setRuleDetails({ ...ruleDetails, items: v })}
                    getOptionLabel={(option) => option?.productItemId && option?.name ? `${option.productItemId} - ${option.name}` : option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.productItemId === value?.productItemId}
                    disabled={ruleDetails.catalogIds.length == 0 && ruleDetails.rules.length == 0}

                    renderInput={(params) =>
                      <TextField {...params} label="Products" size="small"
                        helperText="Select catalog / Rule first" />
                    }

                  />
                </FormControl>
              </Grid>

            </Grid>

          </>
        </CardContent>
      </Card >
      {/* Sticky Pagination */}
      {results?.length > 0 && (
        <Box
          sx={{
            margin: "auto",
            mt: 2,
            height: "70vh",
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            overflow: "auto",
            background: "#fafafa",
            pb: 10
          }}
        >
          {/* Sticky Pagination */}
          {results.length > pageSize && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "white",
                borderBottom: "1px solid #E5E7EB",
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} • {results.length} Results
              </Typography>

              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Results */}
          <ResultCards results={paginatedResults} />
        </Box>
      )}
      <Message
        open={apiMessage.open}
        type={apiMessage.type}
        message={apiMessage.message}
        onClose={() => setApiMessage((p) => ({ ...p, open: false }))}
      />
    </Box>
  );
}